import {
  IconBigSmallErrorButtonStyles,
  IconBigSmallPrimaryButtonStyles,
} from '../components/V2Buttons/IconBigSmallButton/Styles'
import i18n from '../i18n'

/**
 * color hex to rgba
 * @param input
 * @param control: add number if you want a little bit changing color
 * @param opacity: number only 0~1
 * @returns
 */
export const hexToRgba = (
  input: number | string,
  control?: number,
  transparency?: number,
) => {
  const hex6 = String(input).trim().replace('#', '')

  const rgb =
    hex6.length === 3 ? hex6.match(/[a-f\d]/gi) : hex6.match(/[a-f\d]{2}/gi)

  if (rgb) {
    rgb.forEach((str, x, arr) => {
      // 헥사값이 한자리일 경우 두자리로 변경
      if (str.length === 1) str += str

      // 10진수로 변환
      arr[x] = String(parseInt(str, 16))

      if (control) {
        arr[x] =
          Number(arr[x]) + control > 255
            ? '255'
            : String(Number(arr[x]) + control)
      }
    })
  }

  const alpha: number = transparency ?? 1

  return `${rgb?.join(', ') ?? '0, 0, 0'}, ${alpha > 1 ? 1 : alpha}`
}

/**
 * - open이 토글되는 특정 엘리먼트(셀렉트 박스 등)를 열고 다른 영역에서 마우스 다운하면 닫는 함수
 * - 참고: V2Select - SpreadableSelectBox.tsx
 * @param e : event
 * @param ref : want close element
 * @param setState : want change boolean state: true to be false
 */
export const handleClickOutside = (
  e: MouseEvent | TouchEvent,
  ref: React.RefObject<any>,
  setState: (value: React.SetStateAction<boolean>) => void,
) => {
  // when ref opened and other side mousedown
  if (ref.current !== null && !ref.current.contains(e.target as Node)) {
    setState(false)
  }
}

export const getIconButtonTheme = (progress: string | boolean) => {
  // if (progress === 'Success') return IconBigSmallPrimaryButtonStyles
  // if (progress === 'ICA') return IconBigSmallPrimaryButtonStyles
  // if (progress === 'Post processing') return IconBigSmallPrimaryButtonStyles
  // if (progress === 'Completed') return IconBigSmallPrimaryButtonStyles
  // if (progress === 'Error') return IconBigSmallErrorButtonStyles
  // return IconBigSmallErrorButtonStyles
  if (progress === i18n.t('ISuccess')) return IconBigSmallPrimaryButtonStyles
  if (progress === i18n.t('IStatus_ica')) return IconBigSmallPrimaryButtonStyles
  if (progress === i18n.t('IPostProcessing'))
    return IconBigSmallPrimaryButtonStyles
  if (progress === i18n.t('IStatus_success'))
    return IconBigSmallPrimaryButtonStyles
  if (progress === i18n.t('IError')) return IconBigSmallErrorButtonStyles

  if (progress === true) return IconBigSmallPrimaryButtonStyles
  return IconBigSmallErrorButtonStyles
  // return IconBigSmallDisabledButtonStyles
}

export const getUniqueKey = (index: number): string => {
  return `${index}${Math.floor(Math.random() * 10000)}`
}

export const formatPriceNumber = (price: number, locale = 'en-US'): string => {
  return price.toLocaleString(locale)
}

export const parsePriceNumber = (
  formattedValue: string,
  locale = 'en-US',
): number => {
  if (!formattedValue) return 0

  // 1. 해당 locale에서 사용하는 천 단위 구분자와 소수점 구분자 알아내기
  const parts = new Intl.NumberFormat(locale).formatToParts(1111.1)
  const groupChar = parts.find((part) => part.type === 'group')?.value || ','
  const decimalChar =
    parts.find((part) => part.type === 'decimal')?.value || '.'

  // 2. 입력된 문자열에서 천 단위 구분자 모두 제거
  // (replaceAll을 사용하거나 정규식을 사용)
  let unformatted = formattedValue.split(groupChar).join('')

  // 3. 지역별 소수점 구분자를 자바스크립트 표준인 '.'으로 변경
  unformatted = unformatted.replace(decimalChar, '.')

  // 4. 순수 숫자로 변환
  const parsed = parseFloat(unformatted)

  return Number.isNaN(parsed) ? 0 : parsed
}

export const getCountryFromCountryCode = (code: string) => {
  const countryNames: Record<string, string> = {
    AF: 'Afghanistan',
    AX: 'Åland Islands',
    AL: 'Albania',
    DZ: 'Algeria',
    AS: 'American Samoa',
    AD: 'Andorra',
    AO: 'Angola',
    AI: 'Anguilla',
    AQ: 'Antarctica',
    AG: 'Antigua and Barbuda',
    AR: 'Argentina',
    AM: 'Armenia',
    AW: 'Aruba',
    AU: 'Australia',
    AT: 'Austria',
    AZ: 'Azerbaijan',
    BS: 'Bahamas',
    BH: 'Bahrain',
    BD: 'Bangladesh',
    BB: 'Barbados',
    BY: 'Belarus',
    BE: 'Belgium',
    BZ: 'Belize',
    BJ: 'Benin',
    BM: 'Bermuda',
    BT: 'Bhutan',
    BO: 'Bolivia',
    BQ: 'Bonaire',
    BA: 'Bosnia and Herzegovina',
    BW: 'Botswana',
    BV: 'Bouvet Island',
    BR: 'Brazil',
    IO: 'British Indian Ocean Territory',
    BN: 'Brunei Darussalam',
    BG: 'Bulgaria',
    BF: 'Burkina Faso',
    BI: 'Burundi',
    CV: 'Cabo Verde',
    KH: 'Cambodia',
    CM: 'Cameroon',
    CA: 'Canada',
    KY: 'Cayman Islands',
    CF: 'Central African Republic',
    TD: 'Chad',
    CL: 'Chile',
    CN: 'China',
    CX: 'Christmas Island',
    CC: 'Cocos (Keeling) Islands',
    CO: 'Colombia',
    KM: 'Comoros',
    CG: 'Congo',
    CD: 'Congo (Democratic Republic of the)',
    CK: 'Cook Islands',
    CR: 'Costa Rica',
    CI: "Côte d'Ivoire",
    HR: 'Croatia',
    CU: 'Cuba',
    CW: 'Curaçao',
    CY: 'Cyprus',
    CZ: 'Czechia',
    DK: 'Denmark',
    DJ: 'Djibouti',
    DM: 'Dominica',
    DO: 'Dominican Republic',
    EC: 'Ecuador',
    EG: 'Egypt',
    SV: 'El Salvador',
    GQ: 'Equatorial Guinea',
    ER: 'Eritrea',
    EE: 'Estonia',
    SZ: 'Eswatini',
    ET: 'Ethiopia',
    FK: 'Falkland Islands',
    FO: 'Faroe Islands',
    FJ: 'Fiji',
    FI: 'Finland',
    FR: 'France',
    GF: 'French Guiana',
    PF: 'French Polynesia',
    TF: 'French Southern Territories',
    GA: 'Gabon',
    GM: 'Gambia',
    GE: 'Georgia',
    DE: 'Germany',
    GH: 'Ghana',
    GI: 'Gibraltar',
    GR: 'Greece',
    GL: 'Greenland',
    GD: 'Grenada',
    GP: 'Guadeloupe',
    GU: 'Guam',
    GT: 'Guatemala',
    GG: 'Guernsey',
    GN: 'Guinea',
    GW: 'Guinea-Bissau',
    GY: 'Guyana',
    HT: 'Haiti',
    HM: 'Heard Island and McDonald Islands',
    VA: 'Holy See',
    HN: 'Honduras',
    HK: 'Hong Kong',
    HU: 'Hungary',
    IS: 'Iceland',
    IN: 'India',
    ID: 'Indonesia',
    IR: 'Iran',
    IQ: 'Iraq',
    IE: 'Ireland',
    IM: 'Isle of Man',
    IL: 'Israel',
    IT: 'Italy',
    JM: 'Jamaica',
    JP: 'Japan',
    JE: 'Jersey',
    JO: 'Jordan',
    KZ: 'Kazakhstan',
    KE: 'Kenya',
    KI: 'Kiribati',
    KP: 'Korea',
    KR: 'Korea',
    KW: 'Kuwait',
    KG: 'Kyrgyzstan',
    LA: 'Lao People’s Democratic Republic',
    LV: 'Latvia',
    LB: 'Lebanon',
    LS: 'Lesotho',
    LR: 'Liberia',
    LY: 'Libya',
    LI: 'Liechtenstein',
    LT: 'Lithuania',
    LU: 'Luxembourg',
    MO: 'Macao',
    MG: 'Madagascar',
    MW: 'Malawi',
    MY: 'Malaysia',
    MV: 'Maldives',
    ML: 'Mali',
    MT: 'Malta',
    MH: 'Marshall Islands',
    MQ: 'Martinique',
    MR: 'Mauritania',
    MU: 'Mauritius',
    YT: 'Mayotte',
    MX: 'Mexico',
    FM: 'Micronesia',
    MD: 'Moldova',
    MC: 'Monaco',
    MN: 'Mongolia',
    ME: 'Montenegro',
    MS: 'Montserrat',
    MA: 'Morocco',
    MZ: 'Mozambique',
    MM: 'Myanmar',
    NA: 'Namibia',
    NR: 'Nauru',
    NP: 'Nepal',
    NL: 'Netherlands',
    NC: 'New Caledonia',
    NZ: 'New Zealand',
    NI: 'Nicaragua',
    NE: 'Niger',
    NG: 'Nigeria',
    NU: 'Niue',
    NF: 'Norfolk Island',
    MP: 'Northern Mariana Islands',
    NO: 'Norway',
    OM: 'Oman',
    PK: 'Pakistan',
    PW: 'Palau',
    PS: 'Palestine, State of',
    PA: 'Panama',
    PG: 'Papua New Guinea',
    PY: 'Paraguay',
    PE: 'Peru',
    PH: 'Philippines',
    PN: 'Pitcairn',
    PL: 'Poland',
    PT: 'Portugal',
    PR: 'Puerto Rico',
    QA: 'Qatar',
    RE: 'Réunion',
    RO: 'Romania',
    RU: 'Russian Federation',
    RW: 'Rwanda',
    BL: 'Saint Barthélemy',
    SH: 'Saint Helena, Ascension and Tristan da Cunha',
    KN: 'Saint Kitts and Nevis',
    LC: 'Saint Lucia',
    MF: 'Saint Martin',
    PM: 'Saint Pierre and Miquelon',
    VC: 'Saint Vincent and the Grenadines',
    WS: 'Samoa',
    SM: 'San Marino',
    ST: 'Sao Tome and Principe',
    SA: 'Saudi Arabia',
    SN: 'Senegal',
    RS: 'Serbia',
    SC: 'Seychelles',
    SL: 'Sierra Leone',
    SG: 'Singapore',
    SX: 'Sint Maarten',
    SK: 'Slovakia',
    SI: 'Slovenia',
    SB: 'Solomon Islands',
    SO: 'Somalia',
    ZA: 'South Africa',
    GS: 'South Georgia and the South Sandwich Islands',
    SS: 'South Sudan',
    ES: 'Spain',
    LK: 'Sri Lanka',
    SD: 'Sudan',
    SR: 'Suriname',
    SJ: 'Svalbard and Jan Mayen',
    SE: 'Sweden',
    CH: 'Switzerland',
    SY: 'Syrian Arab Republic',
    TW: 'Taiwan, Province of China',
    TJ: 'Tajikistan',
    TZ: 'Tanzania, United Republic of',
    TH: 'Thailand',
    TL: 'Timor-Leste',
    TG: 'Togo',
    TK: 'Tokelau',
    TO: 'Tonga',
    TT: 'Trinidad and Tobago',
    TN: 'Tunisia',
    TR: 'Türkiye',
    TM: 'Turkmenistan',
    TC: 'Turks and Caicos Islands',
    TV: 'Tuvalu',
    UG: 'Uganda',
    UA: 'Ukraine',
    AE: 'United Arab Emirates',
    GB: 'United Kingdom of Great Britain and Northern Ireland',
    US: 'United States of America',
    UM: 'United States Minor Outlying Islands',
    UY: 'Uruguay',
    UZ: 'Uzbekistan',
    VU: 'Vanuatu',
    VE: 'Venezuela',
    VN: 'Viet Nam',
    VG: 'Virgin Islands (British)',
    VI: 'Virgin Islands (U.S.)',
    WF: 'Wallis and Futuna',
    EH: 'Western Sahara',
    YE: 'Yemen',
    ZM: 'Zambia',
    ZW: 'Zimbabwe',
  }
  const name = countryNames[code.toUpperCase()]
  return name || ''
}

/*
  - handleClickOutside 추가할 컴포넌트에서

  // ref 생성
  const myRef = React.useRef<HTMLDivElement>(null)

  // boolean state 생성
  const [isOpen, setIsOpen] = React.useState(false)

  // mousedown 이벤트 추가, isOpen 바뀔 때 마다 실행
  useEffect(() => {
    // ref가 open 상태일 때 실행
    if (isOpen) {
      window.addEventListener('mousedown', (e) =>
        handleClickOutside(e, myRef, setIsOpen),
      )
    }

    // 중첩 실행 방지
    return () => {
      window.removeEventListener('mousedown', (e) =>
        handleClickOutside(e, myRef, setIsOpen),
      )
    }
  }, [isOpen])
*/
